import { BackButton } from "../../components/BackButton";

export interface DownloadEntry {
  subject: string;
  state: "downloaded" | "downloading" | "not-downloaded";
  /**
   * What this pack takes up, already formatted — "74 KB", "1.2 MB".
   *
   * A number of megabytes was wrong twice over: a 75 KB pack rounds to either
   * "0 MB" or "1 MB", and a subject that has not been downloaded has no size to
   * report at all. Null says that honestly instead of printing a zero.
   */
  size: string | null;
  /** 0–100 while downloading. */
  progress?: number;
}

interface DownloadsScreenProps {
  entries: DownloadEntry[];
  /** Total on the device, formatted. */
  totalUsed: string;
  autoUpdateOnWifi: boolean;
  onToggleAutoUpdate: (enabled: boolean) => void;
  pendingSyncCount: number;
  /** Tapping a not-downloaded subject fetches and stores its pack. */
  onDownload?: ((subject: string) => void) | undefined;
  /** Why the last download did not finish. */
  error?: string | null | undefined;
}

/**
 * Screen 19 — offline packs.
 *
 * The pending-sync row is the important one. A student who practised on the bus
 * needs to know those answers are not lost, so it says where they are —
 * "safe on this phone" — rather than showing a worrying unsynced badge.
 */
export function DownloadsScreen({
  entries,
  totalUsed,
  autoUpdateOnWifi,
  onToggleAutoUpdate,
  pendingSyncCount,
  onDownload,
  error,
}: DownloadsScreenProps) {
  return (
    <div className="app-column px-5 pb-6 pt-6">
      <BackButton to="/practice" />

      <div className="flex items-baseline justify-between">
        <h1 className="m-0 font-[family-name:var(--font-display)] text-[20px] font-bold">
          Offline practice
        </h1>
        <span className="tabular text-[12.5px] text-text-muted">{totalUsed} used</span>
      </div>

      <ul className="m-0 flex list-none flex-col gap-2 p-0 pt-5">
        {entries.map((entry) => (
          <li
            key={entry.subject}
            className="flex flex-col gap-2 rounded-[var(--radius-control)] border border-border bg-surface px-3.5 py-3"
          >
            <div className="flex items-center justify-between gap-3">
              {entry.state === "not-downloaded" && onDownload ? (
                <button
                  type="button"
                  onClick={() => onDownload(entry.subject)}
                  className="text-left text-[14px] font-semibold text-accent"
                >
                  {entry.subject}
                </button>
              ) : (
                <span className="text-[14px]">{entry.subject}</span>
              )}
              <span className="tabular text-[12px] text-text-muted">
                {entry.state === "downloaded"
                  ? `Downloaded${entry.size ? ` · ${entry.size}` : ""}`
                  : entry.state === "downloading"
                    ? `Downloading… ${entry.progress ?? 0}%`
                    : "Tap to download"}
              </span>
            </div>
            {entry.state === "downloading" ? (
              <div className="h-1 w-full overflow-hidden rounded-[var(--radius-chip)] bg-surface-high">
                <div
                  className="h-full rounded-[var(--radius-chip)] bg-accent"
                  style={{ width: `${entry.progress ?? 0}%` }}
                />
              </div>
            ) : null}
          </li>
        ))}
      </ul>

      {error ? (
        <p
          role="alert"
          className="m-0 mt-4 rounded-[var(--radius-control)] border border-border bg-surface px-3.5 py-3 text-[12.5px] text-negative"
        >
          {error}
        </p>
      ) : null}

      <label className="flex items-center justify-between gap-3 pt-6 text-[13.5px]">
        Keep my subjects updated on Wi-Fi automatically
        <input
          type="checkbox"
          checked={autoUpdateOnWifi}
          onChange={(event) => onToggleAutoUpdate(event.target.checked)}
          className="h-5 w-5 shrink-0 accent-[var(--accent)]"
        />
      </label>

      {pendingSyncCount > 0 ? (
        <p className="tabular m-0 mt-6 rounded-[var(--radius-control)] border border-border bg-surface px-3.5 py-3 text-[12.5px] text-text-muted">
          <span className="font-semibold text-text">{pendingSyncCount} answers</span> waiting to
          sync — safe on this phone.
        </p>
      ) : null}
    </div>
  );
}
